import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { toSupabaseDate, fromUTCToSaoPaulo } from '../utils/dateUtils';
import './EventModal.css';
import AppIcon from './icons/AppIcon';

export default function EventModal({ event, onClose, onSave, onDelete, projects = [], onOpenContact }) {
  const isNewEvent = !event;
  
  // Normalizar dados do evento para aceitar ambos formatos (startDate do CalendarPage ou start_date do KanbanBoard)
  const normalizedEvent = event ? {
    ...event,
    startDate: event.startDate || event.start_date,
    endDate: event.endDate || event.end_date
  } : null;
  
  // Buscar contact_id de várias fontes possíveis
  const contactId = normalizedEvent?.contact_id || normalizedEvent?.custom_fields?.contact_id || normalizedEvent?.custom_fields?.contactId;
  
  // Buscar dados do contato se houver contact_id
  const [contactData, setContactData] = useState(null);
  const [loadingContact, setLoadingContact] = useState(false);
  
  useEffect(() => {
    if (contactId) {
      loadContactData(contactId);
    }
  }, [contactId]);
  
  const loadContactData = async (contactId) => {
    if (!contactId || loadingContact) return;
    
    setLoadingContact(true);
    try {
      const { data, error } = await supabase
        .from('contatos')
        .select('*')
        .eq('id', contactId)
        .single();
      
      if (!error && data) {
        setContactData(data);
      }
    } catch (err) {
      console.error('Erro ao carregar dados do contato:', err);
    } finally {
      setLoadingContact(false);
    }
  };
  
  // Verificar se é um evento de agendamento CRM
  const isCrmAppointment = normalizedEvent?.event_type === 'crm_appointment' || normalizedEvent?.custom_fields?.appointment_field;
  
  const [eventData, setEventData] = useState(normalizedEvent || {
    title: '',
    description: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    type: 'task',
    project: '',
    color: '#44e1ff',
    location: '',
    attendees: [],
    reminder: 15,
    priority: 'normal',
    recurring: 'none',
    allDay: false,
    connections: [],
    agents: [],
    custom_fields: {}
  });

  const [newAttendee, setNewAttendee] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [integrationType, setIntegrationType] = useState('connections'); // 'connections' or 'agents'

  const eventTypes = [
    { value: 'meeting', label: 'Reunião', icon: 'groups', color: '#3b82f6' },
    { value: 'deadline', label: 'Prazo', icon: 'flag', color: '#ef4444' },
    { value: 'task', label: 'Tarefa', icon: 'task', color: '#44e1ff' },
    { value: 'event', label: 'Evento', icon: 'event', color: '#10b981' },
    { value: 'milestone', label: 'Marco', icon: 'stars', color: '#f59e0b' }
  ];

  const reminderOptions = [
    { value: 0, label: 'Sem lembrete' },
    { value: 5, label: '5 minutos antes' },
    { value: 15, label: '15 minutos antes' },
    { value: 30, label: '30 minutos antes' },
    { value: 60, label: '1 hora antes' },
    { value: 1440, label: '1 dia antes' },
    { value: 10080, label: '1 semana antes' }
  ];

  const recurringOptions = [
    { value: 'none', label: 'Não repetir' },
    { value: 'daily', label: 'Diariamente' },
    { value: 'weekly', label: 'Semanalmente' },
    { value: 'monthly', label: 'Mensalmente' },
    { value: 'yearly', label: 'Anualmente' }
  ];

  const priorityOptions = [
    { value: 'baixa', label: 'Baixa', color: '#10b981' },
    { value: 'normal', label: 'Normal', color: '#3b82f6' },
    { value: 'alta', label: 'Alta', color: '#f59e0b' },
    { value: 'urgente', label: 'Urgente', color: '#ef4444' }
  ];

  // Mock data - será substituído por dados reais da API
  const mockConnections = [
    { id: 1, name: 'Google Calendar', icon: 'calendar_today', status: 'active', color: '#4285F4' },
    { id: 2, name: 'Slack', icon: 'chat', status: 'active', color: '#4A154B' },
    { id: 3, name: 'Microsoft Teams', icon: 'groups', status: 'active', color: '#6264A7' },
    { id: 4, name: 'Zoom', icon: 'videocam', status: 'active', color: '#2D8CFF' },
    { id: 5, name: 'Trello', icon: 'dashboard', status: 'inactive', color: '#0079BF' },
    { id: 6, name: 'Asana', icon: 'task_alt', status: 'active', color: '#F06A6A' }
  ];

  const mockAgents = [
    { id: 1, name: 'Assistente de Agendamento', icon: 'smart_toy', status: 'active', color: '#44e1ff' },
    { id: 2, name: 'Analisador de Conflitos', icon: 'psychology', status: 'active', color: '#b43bea' },
    { id: 3, name: 'Sugestões Inteligentes', icon: 'lightbulb', status: 'active', color: '#f59e0b' },
    { id: 4, name: 'Resumidor de Reuniões', icon: 'summarize', status: 'active', color: '#10b981' },
    { id: 5, name: 'Transcritor de Áudio', icon: 'mic', status: 'inactive', color: '#ef4444' }
  ];

  const handleSave = () => {
    if (!eventData.title.trim()) {
      alert('O título do evento é obrigatório');
      return;
    }

    if (!eventData.startDate) {
      alert('A data de início é obrigatória');
      return;
    }

    // Combinar data e hora (mantendo fuso horário de São Paulo)
    const startDateTime = eventData.allDay 
      ? toSupabaseDate(new Date(eventData.startDate))
      : toSupabaseDate(new Date(`${eventData.startDate}T${eventData.startTime || '00:00'}`));

    const endDateTime = eventData.endDate
      ? (eventData.allDay 
          ? toSupabaseDate(new Date(eventData.endDate))
          : toSupabaseDate(new Date(`${eventData.endDate}T${eventData.endTime || '23:59'}`)))
      : startDateTime;

    const savedEvent = {
      ...eventData,
      id: eventData.id || Date.now().toString(),
      startDate: startDateTime,
      endDate: endDateTime,
      color: eventTypes.find(t => t.value === eventData.type)?.color || eventData.color
    };

    onSave(savedEvent);
    onClose();
  };

  const handleAddAttendee = () => {
    if (newAttendee.trim()) {
      setEventData({
        ...eventData,
        attendees: [...(eventData.attendees || []), newAttendee.trim()]
      });
      setNewAttendee('');
    }
  };

  const handleRemoveAttendee = (index) => {
    setEventData({
      ...eventData,
      attendees: eventData.attendees.filter((_, i) => i !== index)
    });
  };

  const toggleConnection = (connectionId) => {
    const connections = eventData.connections || [];
    const isSelected = connections.includes(connectionId);
    
    setEventData({
      ...eventData,
      connections: isSelected
        ? connections.filter(id => id !== connectionId)
        : [...connections, connectionId]
    });
  };

  const toggleAgent = (agentId) => {
    const agents = eventData.agents || [];
    const isSelected = agents.includes(agentId);
    
    setEventData({
      ...eventData,
      agents: isSelected
        ? agents.filter(id => id !== agentId)
        : [...agents, agentId]
    });
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(eventData.id);
      onClose();
    }
  };

  // Preencher data/hora se estiver editando (converter de UTC para São Paulo)
  React.useEffect(() => {
    if (normalizedEvent && normalizedEvent.startDate) {
      const start = fromUTCToSaoPaulo(normalizedEvent.startDate);
      const end = normalizedEvent.endDate ? fromUTCToSaoPaulo(normalizedEvent.endDate) : start;
      
      setEventData({
        ...normalizedEvent,
        startDate: start.toISOString().split('T')[0],
        startTime: start.toTimeString().slice(0, 5),
        endDate: end.toISOString().split('T')[0],
        endTime: end.toTimeString().slice(0, 5)
      });
    }
  }, [normalizedEvent]);

  // Renderização normal do modal (sempre completo)
  return (
    <div className="modal-overlay-event" onClick={onClose}>
      <div className="event-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="event-modal-header">
          <div className="event-modal-title-section">
            <AppIcon name={eventTypes.find(t => t.value === eventData.type)?.icon || 'event'} className="event-modal-icon" />
            <h2>{isNewEvent ? 'Novo Evento' : 'Editar Evento'}</h2>
          </div>
          <button className="btn-close-modal" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="event-modal-content" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Seção de Agendamento CRM */}
          {isCrmAppointment && (
            <div className="event-section" style={{ background: 'var(--bg-color-secondary)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
              <h4 style={{ color: 'var(--primary-color)', marginBottom: '1rem' }}>
                <AppIcon name="person" />
                Informações do Agendamento
              </h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-color-secondary)', display: 'block', marginBottom: '0.25rem' }}>Cliente</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontWeight: '500', flex: 1 }}>{contactData?.nome || normalizedEvent?.custom_fields?.contact_name || 'Não identificado'}</span>
                    {contactId && onOpenContact && (
                      <button 
                        onClick={() => onOpenContact(contactId)}
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: 'var(--primary-color)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <AppIcon name="open_in_new" style={{ fontSize: '1rem' }} />
                        Ver
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-color-secondary)', display: 'block', marginBottom: '0.25rem' }}>Telefone</label>
                  <div style={{ fontWeight: '500' }}>{contactData?.numero || normalizedEvent?.custom_fields?.contact_phone || '-'}</div>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-color-secondary)', display: 'block', marginBottom: '0.25rem' }}>Email</label>
                  <div style={{ fontWeight: '500' }}>{contactData?.email || normalizedEvent?.custom_fields?.contact_email || '-'}</div>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-color-secondary)', display: 'block', marginBottom: '0.25rem' }}>Projeto</label>
                  <div style={{ fontWeight: '500' }}>{normalizedEvent?.custom_fields?.project_name || normalizedEvent?.project_name || '-'}</div>
                </div>
              </div>
              
              {/* Campos do formulário preenchidos */}
              {normalizedEvent?.custom_fields?.form_fields && Object.keys(normalizedEvent.custom_fields.form_fields).length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ fontSize: '0.75rem', color: 'var(--text-color-secondary)', display: 'block', marginBottom: '0.5rem' }}>Dados do Formulário</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
                    {Object.entries(normalizedEvent.custom_fields.form_fields).map(([key, value]) => {
                      // Formatar o nome da chave para exibição amigável
                      const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                      // Formatar o valor
                      let displayValue = value;
                      if (typeof value === 'object') {
                        displayValue = Array.isArray(value) ? value.join(', ') : JSON.stringify(value);
                      }
                      return (
                        <div key={key} style={{ 
                          padding: '0.75rem', 
                          background: 'var(--bg-color)', 
                          borderRadius: '8px',
                          border: '1px solid var(--border-color)'
                        }}>
                          <div style={{ color: 'var(--text-color-secondary)', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                            {formattedKey}
                          </div>
                          <div style={{ fontWeight: '500', fontSize: '0.9rem', color: 'var(--text-color)' }}>
                            {displayValue || '-'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Coluna Principal */}
          <div className="event-modal-main">
            {/* Informações Básicas */}
            <div className="event-section">
              <h4>
                <AppIcon name="info" />
                Informações Básicas
              </h4>

              <div className="form-group">
                <label>Título *</label>
                <input
                  type="text"
                  value={eventData.title}
                  onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
                  placeholder="Ex: Reunião de Sprint Planning"
                  className="event-input"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  value={eventData.description}
                  onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                  placeholder="Adicione detalhes sobre o evento..."
                  className="event-textarea"
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label>Tipo de Evento</label>
                <div className="event-type-grid">
                  {eventTypes.map(type => (
                    <button
                      key={type.value}
                      className={`event-type-option ${eventData.type === type.value ? 'active' : ''}`}
                      onClick={() => setEventData({ ...eventData, type: type.value })}
                      style={{
                        borderColor: eventData.type === type.value ? type.color : 'transparent',
                        color: eventData.type === type.value ? type.color : '#9bb3c7'
                      }}
                    >
                      <AppIcon name={type.icon} />
                      <span>{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Data e Hora */}
            <div className="event-section">
              <h4>
                <AppIcon name="schedule" />
                Data e Hora
              </h4>

              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={eventData.allDay}
                    onChange={(e) => setEventData({ ...eventData, allDay: e.target.checked })}
                  />
                  <span>Evento de dia inteiro</span>
                </label>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Data de Início *</label>
                  <input
                    type="date"
                    value={eventData.startDate}
                    onChange={(e) => setEventData({ ...eventData, startDate: e.target.value })}
                    className="event-input"
                  />
                </div>

                {!eventData.allDay && (
                  <div className="form-group">
                    <label>Hora de Início</label>
                    <input
                      type="time"
                      value={eventData.startTime}
                      onChange={(e) => setEventData({ ...eventData, startTime: e.target.value })}
                      className="event-input"
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Data de Término</label>
                  <input
                    type="date"
                    value={eventData.endDate}
                    onChange={(e) => setEventData({ ...eventData, endDate: e.target.value })}
                    className="event-input"
                  />
                </div>

                {!eventData.allDay && (
                  <div className="form-group">
                    <label>Hora de Término</label>
                    <input
                      type="time"
                      value={eventData.endTime}
                      onChange={(e) => setEventData({ ...eventData, endTime: e.target.value })}
                      className="event-input"
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Recorrência</label>
                <select
                  value={eventData.recurring || 'none'}
                  onChange={(e) => setEventData({ ...eventData, recurring: e.target.value })}
                  className="event-select"
                >
                  {recurringOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Detalhes Adicionais */}
            <div className="event-section">
              <h4>
                <AppIcon name="more_horiz" />
                Detalhes Adicionais
              </h4>

              <div className="form-group">
                <label>Projeto</label>
                <select
                  value={eventData.project}
                  onChange={(e) => setEventData({ ...eventData, project: e.target.value })}
                  className="event-select"
                >
                  <option value="">Nenhum projeto</option>
                  {projects.map(project => (
                    <option key={project.id || project} value={project.id || project}>
                      {project.name || project}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Local</label>
                <input
                  type="text"
                  value={eventData.location || ''}
                  onChange={(e) => setEventData({ ...eventData, location: e.target.value })}
                  placeholder="Ex: Sala de Reunião 1, Google Meet, etc."
                  className="event-input"
                />
              </div>

              <div className="form-group">
                <label>Prioridade</label>
                <select
                  value={eventData.priority || 'normal'}
                  onChange={(e) => setEventData({ ...eventData, priority: e.target.value })}
                  className="event-select"
                  style={{ borderColor: priorityOptions.find(p => p.value === eventData.priority)?.color }}
                >
                  {priorityOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Participantes</label>
                <div className="attendees-list">
                  {(eventData.attendees || []).map((attendee, index) => (
                    <div key={index} className="attendee-chip">
                      <AppIcon name="person" />
                      <span>{attendee}</span>
                      <button onClick={() => handleRemoveAttendee(index)}>
                        <AppIcon name="close" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="add-attendee-row">
                  <input
                    type="text"
                    value={newAttendee}
                    onChange={(e) => setNewAttendee(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddAttendee()}
                    placeholder="Adicionar participante..."
                    className="event-input"
                  />
                  <button onClick={handleAddAttendee} className="btn-add-attendee">
                    <AppIcon name="add" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="event-modal-sidebar">
            <h4>Configurações</h4>

            <div className="form-group">
              <label>Lembrete</label>
              <select
                value={eventData.reminder || 15}
                onChange={(e) => setEventData({ ...eventData, reminder: parseInt(e.target.value) })}
                className="event-select"
              >
                {reminderOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Integrações */}
            <h4 style={{ marginTop: '1.5rem' }}>Integrações</h4>
            
            <button 
              className="sidebar-btn-event"
              onClick={() => setShowIntegrations(!showIntegrations)}
            >
              <AppIcon name={showIntegrations ? 'expand_less' : 'expand_more'} />
              {showIntegrations ? 'Ocultar Integrações' : 'Mostrar Integrações'}
            </button>

            {showIntegrations && (
              <div className="integrations-panel">
                {/* Tabs */}
                <div className="integration-tabs">
                  <button
                    className={`integration-tab ${integrationType === 'connections' ? 'active' : ''}`}
                    onClick={() => setIntegrationType('connections')}
                  >
                    <AppIcon name="link" />
                    Conexões
                  </button>
                  <button
                    className={`integration-tab ${integrationType === 'agents' ? 'active' : ''}`}
                    onClick={() => setIntegrationType('agents')}
                  >
                    <AppIcon name="smart_toy" />
                    Agentes IA
                  </button>
                </div>

                {/* Connections List */}
                {integrationType === 'connections' && (
                  <div className="integration-list">
                    {mockConnections.map(connection => (
                      <div
                        key={connection.id}
                        className={`integration-item ${
                          (eventData.connections || []).includes(connection.id) ? 'selected' : ''
                        } ${connection.status === 'inactive' ? 'disabled' : ''}`}
                        onClick={() => connection.status === 'active' && toggleConnection(connection.id)}
                      >
                        <AppIcon name={connection.icon} className="integration-icon" style={{ color: connection.color }} />
                        <div className="integration-info">
                          <span className="integration-name">{connection.name}</span>
                          <span className="integration-status">
                            {connection.status === 'active' ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                        {(eventData.connections || []).includes(connection.id) && (
                          <AppIcon name="check_circle" className="check-icon" />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Agents List */}
                {integrationType === 'agents' && (
                  <div className="integration-list">
                    {mockAgents.map(agent => (
                      <div
                        key={agent.id}
                        className={`integration-item ${
                          (eventData.agents || []).includes(agent.id) ? 'selected' : ''
                        } ${agent.status === 'inactive' ? 'disabled' : ''}`}
                        onClick={() => agent.status === 'active' && toggleAgent(agent.id)}
                      >
                        <AppIcon name={agent.icon} className="integration-icon" style={{ color: agent.color }} />
                        <div className="integration-info">
                          <span className="integration-name">{agent.name}</span>
                          <span className="integration-status">
                            {agent.status === 'active' ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                        {(eventData.agents || []).includes(agent.id) && (
                          <AppIcon name="check_circle" className="check-icon" />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Summary */}
                <div className="integration-summary">
                  <span>
                    {(eventData.connections || []).length} conexão(ões) • {(eventData.agents || []).length} agente(s)
                  </span>
                </div>
              </div>
            )}

            <h4 style={{ marginTop: '1.5rem' }}>Ações</h4>

            <button className="sidebar-btn-event" onClick={handleSave}>
              <AppIcon name="save" />
              {isNewEvent ? 'Criar Evento' : 'Salvar Alterações'}
            </button>

            {!isNewEvent && (
              <>
                <button 
                  className="sidebar-btn-event btn-delete-event"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <AppIcon name="delete" />
                  Excluir Evento
                </button>

                {showDeleteConfirm && (
                  <div className="delete-confirm">
                    <p>Confirmar exclusão?</p>
                    <div className="delete-confirm-actions">
                      <button onClick={() => setShowDeleteConfirm(false)}>Cancelar</button>
                      <button onClick={handleDelete} className="btn-confirm-delete">Excluir</button>
                    </div>
                  </div>
                )}
              </>
            )}

            <h4 style={{ marginTop: '1.5rem' }}>Dicas</h4>
            
            <div className="tips-box">
              <p>💡 Use recorrência para eventos regulares</p>
              <p>⏰ Configure lembretes para não esquecer</p>
              <p>👥 Adicione participantes para reuniões</p>
              <p>📍 Inclua o local ou link da reunião</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="event-modal-footer">
          <button className="btn-cancel-event" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn-save-event" onClick={handleSave}>
            <AppIcon name="check" />
            {isNewEvent ? 'Criar Evento' : 'Salvar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}
